import api from './api';

class PasswordValidationService {
  constructor() {
    this.config = null;
  }

  /**
   * Get password validation configuration from the server
   */
  async getPasswordConfig() {
    try {
      const response = await api.get('/auth/password-config');
      this.config = response.data;
      return this.config;
    } catch (error) {
      console.error('Failed to get password config:', error);
      // Return default config if server request fails
      return {
        enabled: false,
        confirmation_required: false
      };
    }
  }

  /**
   * Validate password on the frontend
   */
  validatePassword(password, confirmPassword = null) {
    if (!this.config || !this.config.enabled) {
      return {
        valid: true,
        errors: [],
        requirements: {}
      };
    }

    const errors = [];
    const requirements = {};

    // Check minimum length
    const minLengthMet = password.length >= this.config.min_length;
    requirements.min_length = minLengthMet;
    if (!minLengthMet) {
      errors.push(`Password must be at least ${this.config.min_length} characters long`);
    }

    // Check uppercase requirement
    if (this.config.require_uppercase) {
      const hasUppercase = /[A-Z]/.test(password);
      requirements.uppercase = hasUppercase;
      if (!hasUppercase) {
        errors.push('Password must contain at least one uppercase letter');
      }
    }

    // Check lowercase requirement
    if (this.config.require_lowercase) {
      const hasLowercase = /[a-z]/.test(password);
      requirements.lowercase = hasLowercase;
      if (!hasLowercase) {
        errors.push('Password must contain at least one lowercase letter');
      }
    }

    // Check numbers requirement
    if (this.config.require_numbers) {
      const hasNumbers = /\d/.test(password);
      requirements.numbers = hasNumbers;
      if (!hasNumbers) {
        errors.push('Password must contain at least one number');
      }
    }

    // Check special characters requirement
    if (this.config.require_special) {
      const specialChars = this.config.special_chars || '!@#$%^&*()_+-=[]{}|;:,.<>?';
      // Escape every regex metachar INCLUDING '-'. Without escaping '-', the
      // substring "+-=" becomes a range (chars 43-61) that matches digits, so
      // passwords with no real special char were wrongly accepted client-side
      // and then rejected by the server with a 400.
      const specialCharsRegex = new RegExp(`[${specialChars.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&')}]`);
      const hasSpecial = specialCharsRegex.test(password);
      requirements.special = hasSpecial;
      if (!hasSpecial) {
        errors.push('Password must contain at least one special character');
      }
    }

    // Check password confirmation
    if (this.config.confirmation_required && confirmPassword !== null) {
      const passwordsMatch = password === confirmPassword;
      requirements.confirmation = passwordsMatch;
      if (!passwordsMatch) {
        errors.push('Passwords do not match');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      requirements
    };
  }

  /**
   * Get password requirements as a formatted list for display
   */
  getPasswordRequirementsText() {
    if (!this.config || !this.config.enabled) {
      return [];
    }

    const requirements = [];

    if (this.config.min_length) {
      requirements.push(`At least ${this.config.min_length} characters`);
    }

    if (this.config.require_uppercase) {
      requirements.push('One uppercase letter');
    }

    if (this.config.require_lowercase) {
      requirements.push('One lowercase letter');
    }

    if (this.config.require_numbers) {
      requirements.push('One number');
    }

    if (this.config.require_special) {
      requirements.push('One special character');
    }

    return requirements;
  }

  /**
   * Check if password validation is enabled
   */
  isEnabled() {
    return this.config && this.config.enabled;
  }

  /**
   * Check if password confirmation is required
   */
  isConfirmationRequired() {
    return this.config && this.config.confirmation_required;
  }
}

export default new PasswordValidationService();
